"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DistributionYield = exports.Distribution = exports.DistributionKind = void 0;
const react_1 = require("react");
const accountParser_1 = require("./../common/accountParser");
const anchor_1 = require("@project-serum/anchor");
const web3_js_1 = require("@solana/web3.js");
const common_1 = require("../common");
const hooks_1 = require("../common/hooks");
const client_1 = require("./client");
const lodash_1 = __importDefault(require("lodash"));
const spl_token_1 = require("@solana/spl-token");
class DistributionKind {
    constructor() {
        this.linear = {};
    }
}
exports.DistributionKind = DistributionKind;
class Distribution {
    /**
     * Creates an instance of Distribution.
     * @param {DistributionAddresses} addresses
     * @param {DistributionInfo} distribution
     * @param {AssociatedToken} vault
     * @memberof Distribution
     */
    constructor(addresses, distribution, vault) {
        this.addresses = addresses;
        this.distribution = distribution;
        this.vault = vault;
    }
    static derive(seed, seedLen) {
        if (seed instanceof Array) {
            if (!seedLen) {
                throw new Error("Seed length required");
            }
            seed = (0, accountParser_1.utf8ToString)(seed, seedLen);
        }
        const distribution = (0, common_1.findDerivedAccount)(client_1.RewardsClient.PROGRAM_ID, "distribution", seed);
        const vault = (0, common_1.findDerivedAccount)(client_1.RewardsClient.PROGRAM_ID, distribution, "vault");
        return { distribution, vault };
    }
    /**
     * Load the distribution account and its vault
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {string} seed
     * @returns {Promise<Distribution>}
     * @memberof Distribution
     */
    static async load(rewardsProgram, seed) {
        const addresses = Distribution.derive(seed);
        const vault = await common_1.AssociatedToken.loadAux(rewardsProgram.provider.connection, addresses.vault);
        const distribution = (await rewardsProgram.account.distribution.fetch(addresses.distribution));
        if (!vault) {
            throw new Error("Vault is undefined");
        }
        return new Distribution(addresses, distribution, vault);
    }
    /**
     * TODO:
     *
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @returns {Promise<Distribution[]>}
     * @memberof Distribution
     */
    static async loadAll(rewardsProgram) {
        const distributions = (await rewardsProgram.account.distribution.all());
        const addresses = distributions.map(dist => Distribution.derive(dist.account.seed, dist.account.seedLen));
        const vaultAddresses = addresses.map(address => address.vault);
        const vaults = await common_1.AssociatedToken.loadMultipleAux(rewardsProgram.provider.connection, vaultAddresses);
        return (lodash_1.default.zip(addresses, distributions, vaults)
            .filter(([address, distribution, vault]) => address && distribution && vault)
            .map(([address, distribution, vault]) => {
            if (!address) {
                throw new Error("Address is undefined");
            }
            if (!distribution) {
                throw new Error("Distribution is undefined");
            }
            if (!vault) {
                throw new Error("Vault is undefined");
            }
            return new Distribution(address, distribution.account, vault);
        })
            // Sort by beginAt descending
            .sort((a, b) => b.distribution.tokenDistribution.beginAt.sub(a.distribution.tokenDistribution.beginAt).toNumber()));
    }
    /**
     * TODO:
     *
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {StakePool} distributionTarget
     * @param {DistributionCreateParams} params
     * @memberof Distribution
     */
    static async createForStakePool(rewardsProgram, distributionTarget, params) {
        const stakePoolVault = distributionTarget.addresses.stakePoolVault;
        const tokenMint = distributionTarget.stakePool.tokenMint;
        await this.create(rewardsProgram, stakePoolVault, tokenMint, params);
    }
    /**
     * TODO:
     *
     * @static
     * @param {Program<RewardsIdl>} rewardsProgram
     * @param {PublicKey} targetAccount
     * @param {PublicKey} tokenMint
     * @param {DistributionCreateParams} params
     * @returns {Promise<string>}
     * @memberof Distribution
     */
    static async create(rewardsProgram, targetAccount, tokenMint, params) {
        const addresses = this.derive(params.args.seed);
        return rewardsProgram.methods
            .distributionCreate(Object.assign(Object.assign({}, params.args), { targetAccount }))
            .accounts(Object.assign(Object.assign({}, params.accounts), { distribution: addresses.distribution, vault: addresses.vault, payerRent: rewardsProgram.provider.wallet.publicKey, tokenMint, tokenProgram: spl_token_1.TOKEN_PROGRAM_ID, systemProgram: web3_js_1.SystemProgram.programId, rent: web3_js_1.SYSVAR_RENT_PUBKEY }))
            .rpc();
    }
    /**
     * React hook to load the distribution account and its vault
     * @static
     * @param {(Program<RewardsIdl> | undefined)} rewardsProgram
     * @param {string} seed
     * @returns {Distribution | undefined}
     * @memberof Distribution
     */
    static use(rewardsProgram, seed) {
        return hooks_1.Hooks.usePromise(async () => rewardsProgram && Distribution.load(rewardsProgram, seed), [rewardsProgram, seed]);
    }
    /**
     * React hook to load all the distribution accounts and their vaults
     * @static
     * @param {(Program<RewardsIdl> | undefined)} rewardsProgram
     * @returns {Distribution[] | undefined}
     * @memberof Distribution
     */
    static useAll(rewardsProgram) {
        return hooks_1.Hooks.usePromise(async () => rewardsProgram && Distribution.loadAll(rewardsProgram), [rewardsProgram]);
    }
    /**
     * TODO:
     *
     * @static
     * @param {(Distribution[] | undefined)} distributions
     * @returns {(Distribution[] | undefined)}
     * @memberof Distribution
     */
    static useActive(distributions) {
        return (0, react_1.useMemo)(() => {
            if (!distributions) {
                return undefined;
            }
            const now = new anchor_1.BN(new Date().getTime() / 1000);
            return (distributions
                // Ignore inactive proposals
                .filter(dist => dist.isActive(now)));
        }, [distributions]);
    }
    /**
     * TODO:
     *
     * @param {BN} now
     * @returns {boolean}
     * @memberof Distribution
     */
    isActive(now) {
        if (this.distribution.tokenDistribution.beginAt.gt(now)) {
            return false;
        }
        if (this.distribution.tokenDistribution.endAt.lte(now)) {
            return false;
        }
        return true;
    }
    /**
     * TODO:
     *
     * @static
     * @param {Distribution} dist
     * @param {number} totalDeposits
     * @param {number} totalShares
     * @param {number} usersShares
     * @returns {DistributionYield}
     * @memberof Distribution
     */
    static estimateYield(dist, totalDeposits, totalShares, usersShares) {
        const tokenDist = dist.distribution.tokenDistribution;
        if (tokenDist.kind.linear === undefined) {
            throw new Error("Calculatng non linear yield not implemented.");
        }
        const day = 24 * 3600;
        const year = day * 365.25;
        const distAmount = (0, accountParser_1.bnToNumber)(tokenDist.targetAmount);
        const duration = (0, accountParser_1.bnToNumber)(tokenDist.endAt.sub(tokenDist.beginAt));
        const durationInYears = duration / year;
        const equityPercent = totalShares !== 0 ? usersShares / totalShares : 1;
        const perDay = (distAmount / duration) * equityPercent * day;
        const perMonth = (distAmount / duration) * equityPercent * day * 30.4375;
        const perYear = (distAmount / duration) * equityPercent * day * 365.25;
        const apr = distAmount / totalDeposits / durationInYears;
        return new DistributionYield(apr, perDay, perMonth, perYear);
    }
    /**
     * TODO:
     *
     * @static
     * @param {Distribution[]} distributions
     * @param {number} totalDeposits
     * @param {number} totalShares
     * @param {number} usersShares
     * @returns {DistributionYield}
     * @memberof Distribution
     */
    static estimateCombinedYield(distributions, totalDeposits, totalShares, usersShares) {
        const initialValue = new DistributionYield();
        return distributions.reduce((combinedYield, dist) => {
            return Distribution.estimateYield(dist, totalDeposits, totalShares, usersShares).combine(combinedYield);
        }, initialValue);
    }
    /**
     * TODO:
     *
     * @static
     * @param {(Distribution[] | undefined)} distributions
     * @param {(StakePool | undefined)} stakePool
     * @param {(StakeAccount | undefined)} stakeAccount
     * @returns {(DistributionYield | undefined)}
     * @memberof Distribution
     */
    static useEstimateCombinedYield(distributions, stakePool, stakeAccount) {
        return (0, react_1.useMemo)(() => {
            if (!distributions || !stakePool) {
                return undefined;
            }
            let usersShares;
            const totalShares = (0, accountParser_1.bnToNumber)(stakePool.stakePool.bonded.shares);
            const totalDeposits = (0, accountParser_1.bnToNumber)(stakePool.vault.amount);
            if (stakeAccount) {
                usersShares = (0, accountParser_1.bnToNumber)(stakeAccount.stakeAccount.bondedShares);
            }
            else {
                usersShares = 1;
            }
            const combinedYield = Distribution.estimateCombinedYield(distributions, totalDeposits, totalShares, usersShares);
            if (stakePool) {
                return combinedYield;
            }
            return new DistributionYield(combinedYield.apr);
        }, [distributions, stakePool, stakeAccount]);
    }
}
exports.Distribution = Distribution;
class DistributionYield {
    constructor(apr = 0, perDay, perMonth, perYear) {
        this.apr = apr;
        this.perDay = perDay;
        this.perMonth = perMonth;
        this.perYear = perYear;
    }
    /**
     * TODO:
     *
     * @param {DistributionYield} other
     * @returns {DistributionYield}
     * @memberof DistributionYield
     */
    combine(other) {
        var _a, _b, _c, _d, _e, _f;
        return new DistributionYield(this.apr + other.apr, ((_a = this.perDay) !== null && _a !== void 0 ? _a : 0) + ((_b = other.perDay) !== null && _b !== void 0 ? _b : 0), ((_c = this.perMonth) !== null && _c !== void 0 ? _c : 0) + ((_d = other.perMonth) !== null && _d !== void 0 ? _d : 0), ((_e = this.perYear) !== null && _e !== void 0 ? _e : 0) + ((_f = other.perYear) !== null && _f !== void 0 ? _f : 0));
    }
}
exports.DistributionYield = DistributionYield;
//# sourceMappingURL=distribution.js.map